import { Message } from '@ai-sdk/react';
import { generateId } from 'ai';
import React, { useCallback } from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { MessageItem } from './MessageItem';
import { ThinkingMessage } from './ThinkingMessage';
import { WelcomeWrapper } from './WelcomeWrapper';
import { WelcomeList } from './welcome-screens/WelcomeList';

interface MessagesListProps {
  messages: Message[];
  isWidgetChat: boolean;
  isInProgress: boolean;
  status?: 'ready' | 'streaming' | 'submitted' | 'error';
  error?: Error | null;
  textColor?: string;
  memoizedMessages: any[];
  evmAddress?: string;
  options?: {
    agentName?: string;
    agentImage?: string;
    prompt?: string;
    customComponents?: {
      welcomeMessageComponent?: React.ReactNode;
      loadingIndicator?: React.ComponentType<any>;
    };
  };
  widget?: {
    widgetWelcomePrompts?: {
      title?: string;
      description?: string;
      questions?: string[];
      actions?: string[];
    };
  };
  onAppend: (
    message: Message | { id: string; role: 'user'; content: string },
  ) => void;
  onReload: () => void;
  LoadingIndicator?: React.ComponentType<any>;
}

export const MessagesList: React.FC<MessagesListProps> = ({
  messages,
  isWidgetChat,
  status,
  error,
  textColor,
  memoizedMessages,
  evmAddress,
  options,
  widget,
  onAppend,
  onReload,
}) => {
  const widgetWelcomePrompts = widget?.widgetWelcomePrompts;
  const { title, description, questions, actions } = widgetWelcomePrompts || {};

  const renderWidgetWelcome = useCallback(() => {
    if (!isWidgetChat || !messages || messages.length > 0) return null;

    const overlayClasses = cn(
      'bitte-absolute bitte-top-4 bitte-left-4 bitte-z-50',
      'bitte-text-[#b7bbc1] bitte-overflow-y-scroll bitte-max-h-[90%]',
    );

    const titleClasses = cn('bitte-text-[#FAFAFA] bitte-text-2xl bitte-mb-4');

    return (
      <div className={overlayClasses}>
        <p className={titleClasses}>{title || 'Hello'}</p>
        <p className="bitte-mb-8">
          {description ||
            `I'm your ${options?.agentName || 'AI'} agent. What do you want to achieve today?`}
        </p>
        {questions && questions.length > 0 && (
          <div className="bitte-mb-4">
            <WelcomeList
              items={questions}
              label="Questions"
              bgColor="#60A5FA33"
              onItemClick={(question) =>
                onAppend({
                  id: generateId(),
                  role: 'user',
                  content: question,
                })
              }
            />
          </div>
        )}
        {actions && actions.length > 0 && (
          <WelcomeList
            items={actions}
            label="Actions"
            bgColor="#2D1F1C"
            onItemClick={(action) =>
              onAppend({
                id: generateId(),
                role: 'user',
                content: action,
              })
            }
          />
        )}
      </div>
    );
  }, [
    isWidgetChat,
    messages,
    title,
    description,
    questions,
    actions,
    options?.agentName,
    onAppend,
  ]);

  const renderStandardWelcome = useCallback(() => {
    if (isWidgetChat || !messages || messages.length > 0) return null;

    return (
      <WelcomeWrapper
        messages={messages}
        isWidgetChat={isWidgetChat}
        evmAddress={evmAddress}
        append={onAppend}
        options={options}
      />
    );
  }, [isWidgetChat, messages, evmAddress, onAppend, options]);

  const renderError = useCallback(() => {
    if (!error) return null;

    const errorContainerClasses = cn(
      'bitte-flex bitte-flex-col bitte-items-center bitte-justify-center',
      'bitte-space-y-2 bitte-px-6 bitte-pb-6 bitte-text-center bitte-text-sm',
    );

    return (
      <div className={errorContainerClasses}>
        <p>An error occurred.</p>
        <Button
          type="button"
          variant="default"
          size="sm"
          onClick={() => onReload()}
        >
          Retry
        </Button>
      </div>
    );
  }, [error, onReload]);

  const renderMessages = useCallback(() => {
    if (!memoizedMessages || memoizedMessages.length === 0) return null;

    const shouldShowThinking =
      status === 'submitted' &&
      messages &&
      messages.length > 0 &&
      messages[messages.length - 1].role === 'user';

    return (
      <>
        {memoizedMessages.map((messageItemProps, index) => (
          <MessageItem
            key={`${index}-${messageItemProps.message.id}`}
            {...messageItemProps}
          />
        ))}

        {shouldShowThinking && (
          <ThinkingMessage
            agentImage={options?.agentImage}
            agentName={options?.agentName}
            isWidgetMode={isWidgetChat}
            disableAnimation={!!options?.prompt}
          />
        )}
      </>
    );
  }, [
    memoizedMessages,
    status,
    messages,
    options?.agentImage,
    options?.agentName,
    options?.prompt,
    isWidgetChat,
  ]);

  const containerClasses = cn(
    'bitte-flex bitte-w-full bitte-flex-col bitte-gap-6',
  );

  return (
    <>
      {renderWidgetWelcome()}
      {renderStandardWelcome()}
      <div className={containerClasses} style={{ color: textColor }}>
        {renderMessages()}
        {renderError()}
      </div>
    </>
  );
};
